// Dialog „Nachricht planen" (Kanal-Kopfzeile). Vertrag: docs/API.md 3.9.
import { MessageFlags } from '@lagerfeuer/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { scheduledApi, useMaxMessageLength } from '../api';
import { formatDateTime, formatRelative, fromLocalInput, toLocalInput } from '../format';
import type { ExtraContext } from '../index';
import { useStableHandler } from './hooks';
import { toast } from '../toast';
import { Button, Dialog, Field } from '../ui';
import { SCHEDULED_KEY } from './realtime';
import { atDayTime, inMinutes, limitAwareMessage, nextAt, validateSendAt } from './time';
import './scheduled.css';

export function ScheduleMessageDialog({ channelId, onClose: onCloseProp }: ExtraContext) {
  const onClose = useStableHandler(onCloseProp);
  const qc = useQueryClient();
  const maxLength = useMaxMessageLength();
  const [openedAt] = useState(() => Date.now());
  const [content, setContent] = useState('');
  const [when, setWhen] = useState(() => toLocalInput(inMinutes(60, openedAt)));
  const [silent, setSilent] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  const [whenError, setWhenError] = useState<string | null>(null);

  const evening = nextAt(20, 0, openedAt);
  const eveningIsToday = evening.getDate() === new Date(openedAt).getDate();
  const presets = [
    { id: '1h', label: 'In 1 Stunde', at: () => inMinutes(60) },
    { id: 'evening', label: eveningIsToday ? 'Heute Abend 20:00' : 'Morgen Abend 20:00', at: () => nextAt(20) },
    { id: 'tomorrow', label: 'Morgen 9:00', at: () => atDayTime(1, 9) },
  ];

  const create = useMutation({
    mutationFn: (body: { content: string; sendAt: string; flags?: number }) => scheduledApi.create(channelId ?? '', body),
    onSuccess: (dto) => {
      void qc.invalidateQueries({ queryKey: SCHEDULED_KEY });
      toast({ kind: 'success', title: 'Nachricht geplant', body: `Wird ${formatDateTime(dto.sendAt)} gesendet.`, href: '/scheduled' });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = content.trim();
    const cErr = !text ? 'Bitte einen Text eingeben.' : content.length > maxLength ? `Höchstens ${maxLength} Zeichen.` : null;
    const sendAt = fromLocalInput(when);
    const wErr = validateSendAt(sendAt);
    setContentError(cErr);
    setWhenError(wErr);
    if (cErr || wErr || !sendAt || !channelId) return;
    create.mutate({ content, sendAt, ...(silent ? { flags: MessageFlags.SILENT } : {}) });
  };

  const sendAtIso = fromLocalInput(when);
  return (
    <Dialog
      title="Nachricht planen"
      testId="scheduled-dialog"
      onClose={onClose}
      footer={
        <>
          <Button testId="scheduled-dialog-cancel" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" testId="scheduled-dialog-submit" type="submit" form="lfx-schedule-form" disabled={create.isPending || !channelId}>
            {create.isPending ? 'Plane …' : 'Planen'}
          </Button>
        </>
      }
    >
      <form id="lfx-schedule-form" className="lfx-stack" onSubmit={submit} noValidate>
        {!channelId && (
          <div className="lfx-error" role="alert" data-testid="scheduled-dialog-no-channel">
            Kein Kanal ausgewählt.
          </div>
        )}
        <Field
          label="Nachricht"
          error={contentError}
          hint={
            <span className={content.length > maxLength ? 'lfx-sched-count lfx-sched-count--over' : 'lfx-sched-count'} data-testid="scheduled-dialog-count">
              {content.length} / {maxLength}
            </span>
          }
        >
          {(id) => (
            <textarea
              id={id}
              className="lfx-textarea"
              data-testid="scheduled-dialog-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Was soll später gesendet werden?"
              aria-invalid={contentError ? true : undefined}
            />
          )}
        </Field>
        <Field
          label="Zeitpunkt"
          error={whenError}
          hint={sendAtIso ? `${formatDateTime(sendAtIso)} (${formatRelative(sendAtIso, openedAt)})` : 'Frühestens in 30 Sekunden, spätestens in einem Jahr.'}
        >
          {(id) => (
            <input
              id={id}
              type="datetime-local"
              className="lfx-input"
              data-testid="scheduled-dialog-when"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              aria-invalid={whenError ? true : undefined}
            />
          )}
        </Field>
        <div className="lfx-row" role="group" aria-label="Schnellwahl">
          {presets.map((p) => (
            <Button key={p.id} variant="ghost" testId={`scheduled-dialog-preset-${p.id}`} onClick={() => setWhen(toLocalInput(p.at()))}>
              {p.label}
            </Button>
          ))}
        </div>
        <label className="lfx-check">
          <input type="checkbox" data-testid="scheduled-dialog-silent" checked={silent} onChange={(e) => setSilent(e.target.checked)} />
          Stumm senden (keine Push-/Ton-Benachrichtigung)
        </label>
        {create.error && (
          <div className="lfx-error" role="alert" data-testid="scheduled-dialog-error">
            {limitAwareMessage(create.error, 'Du hast die Höchstzahl offener geplanter Nachrichten erreicht. Brich eine ab oder warte, bis eine gesendet wurde.')}
          </div>
        )}
      </form>
    </Dialog>
  );
}
